import { ReactNode, useState } from "react";
import { DataDefinition } from "../rule-engine-core/Common/Data/DataDefinition";
import { UndefinedDataDefinition } from "../rule-engine-core/Common/Data/Definitions/UndefinedDataDefinition";
import { IDataDefinition } from "../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataUtilities } from "../rule-engine-core/Common/DataUtilities";
import { ObjectDataDefinition } from "../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { DefinitionWithExample } from "../CommonUI/DataDesigner/Components/DefinitionWithExample";
import { JSONUIComponent } from "../CommonUI/DataDesigner/Components/JSONUIComponent";
import { SchemaUIComponent } from "../CommonUI/DataDesigner/Components/SchemaUIComponent";
import { EditModeObjectValueEditor } from "../CommonUI/DataEditing/Object";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";
import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import { UIToggleButtons } from "../CommonUI/BuildlingBlocks/UIToggleButtons";
import { UIStandardLayer } from "../CommonUI/BuildlingBlocks/UIStandardLayer";
import type { AppGlueAny } from "../rule-engine-core/Common/AppGlueAny";

export interface DefinitionEditorProps {
    definition: DataDefinition;
    readOnly?: boolean;
}

export enum IDefinitionEditorMode {
    Definition = "Definition",
    Data = "Data",
    JSON = "JSON",
    SCHEMA = "JSON SCHEMA",
}

export const DataBuilder = function (props: DefinitionEditorProps) {
    const [mode, setMode] = useState<IDefinitionEditorMode>(IDefinitionEditorMode.Definition);

    const [defError, setDefError] = useState<string>("");

    const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);

    function validateUndefinedDefinition(fields: IDataDefinition[]): boolean {
        let validation: boolean = true;
        for (let field of fields) {
            if (field instanceof UndefinedDataDefinition) {
                validation = false;
            } else {
                if (field.fields) {
                    validation = validateUndefinedDefinition(field.fields);
                }
            }
        }
        return validation;
    }

    const onChangeMode = (value: IDefinitionEditorMode) => {
        const validation = validateUndefinedDefinition(props.definition.fields);
        if (validation === true) {
            setMode(value);
        } else {
            setDefError("Please delete all undefined definitions");
            setErrorDialogOpen(true);
        }
    };

    const handleErrorDialogClose = () => {
        setErrorDialogOpen(false);
    };

    const getTabs = () => {
        let isHideExampleDataSide = DataUtilities.containsObjectListField(props.definition);
        let tabData: {
            name: string;
            type: IDefinitionEditorMode;
            tabPanel: () => ReactNode;
        }[] = [];

        tabData.push({
            name: "Definition",
            type: IDefinitionEditorMode.Definition,
            tabPanel: () => {
                return (
                    <DefinitionWithExample
                        definition={props.definition}
                        readOnly={props.readOnly}
                    />
                );
            },
        });

        if (isHideExampleDataSide) {
            tabData.push({
                name: "Example Data",
                type: IDefinitionEditorMode.Data,
                tabPanel: () => {
                    let tempObj = new ObjectDataDefinition();
                    tempObj.fields = props.definition.fields;
                    return (
                        <EditModeObjectValueEditor
                            value={props.definition.value}
                            forData={tempObj}
                            editMode="inline"
                            onUpdate={(newValue: AppGlueAny) => {
                                props.definition.value = newValue;
                                StateManager.changed(props.definition);
                            }}
                        />
                    );
                },
            });
        }

        tabData.push({
            name: "JSON",
            type: IDefinitionEditorMode.JSON,
            tabPanel: () => {
                return <JSONUIComponent definition={props.definition} />;
            },
        });

        tabData.push({
            name: "JSON Schema",
            type: IDefinitionEditorMode.SCHEMA,
            tabPanel: () => {
                return <SchemaUIComponent definition={props.definition} />;
            },
        });

        return tabData;
    };

    return (
        <ObserveState
            listenTo={props.definition}
            control={() => {
                let tabs = getTabs();
                let selectedTab = tabs.find((t) => t.type === mode);
                return (
                    <Box
                        width="100%"
                        height="100%"
                        overflow="auto"
                        display="flex"
                        flexDirection="column"
                    >
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"start"}
                            padding={"10px"}
                            width={"100%"}
                        >
                            <UIToggleButtons
                                options={tabs.map((t) => t.type)}
                                value={mode}
                                onChange={(m) => {
                                    onChangeMode(m as IDefinitionEditorMode);
                                }}
                            />
                        </Box>

                        <Box
                            display={"flex"}
                            overflow={"auto"}
                            padding={"10px"}
                            width={"100%"}
                        >
                            {selectedTab!.tabPanel()}
                        </Box>

                        <UIStandardLayer
                            onClose={handleErrorDialogClose}
                            open={errorDialogOpen}
                            title={"Error"}
                            primaryActionText={"Close"}
                            onPrimaryAction={handleErrorDialogClose}
                            renderUI={() => {
                                return <Alert severity="error">{defError}</Alert>;
                            }}
                        ></UIStandardLayer>
                    </Box>
                );
            }}
        />
    );
};
